import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Image,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import { getAccommodations } from "../../../services/travel/accommodationService";
import { normalizeAccommodationImages } from "./dummyAccommodationImages";
import AccommodationDetailsModal from "./AccommodationDetailsModal";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";
import { PickDenguShareButton } from "../../Businesses/BusinessQrShareActions";

const cardsBorderRadii ="8px";

const PICKDENGU_PUBLIC_WEB_URL = (
  process.env.REACT_APP_PUBLIC_WEB_URL ||
  process.env.REACT_APP_FRONTEND_URL ||
  process.env.REACT_APP_APP_URL ||
  "https://pickdengu.mw"
).replace(/\/+$/, "");

const AccommodationDetail = () => {
  const { theme } = useCustomTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation() as { state?: { accommodation?: any } };

  const [accommodation, setAccommodation] = useState<any>(
    state?.accommodation ?? null,
  );
  const [loading, setLoading] = useState(!state?.accommodation);
  const [active, setActive] = useState(0);

  const {
    isOpen: isBookingModalOpen,
    onOpen: onOpenBookingModal,
    onClose: onCloseBookingModal,
  } = useDisclosure();

  useEffect(() => {
    if (state?.accommodation) {
      setAccommodation(state.accommodation);
      setLoading(false);
    }
  }, [state?.accommodation, id]);

  useEffect(() => {
    if (!id) return;

    if (
      state?.accommodation &&
      String(state.accommodation._id ?? state.accommodation.id) === String(id)
    ) {
      return;
    }

    const fetchAccommodation = async () => {
      setLoading(true);
      try {
        const response = await getAccommodations();
        const data = response.data || [];
        const found = data.find(
          (a: any) =>
            String(a._id) === String(id) || String(a.id) === String(id),
        );

        if (found) {
          const normalized = {
            ...found,
            images: normalizeAccommodationImages(found.images, found.imageUrl),
            availability: found.availability !== false,
            pricePerNight: found.pricePerNight ?? found.price ?? 0,
            type: found.type || found.roomType || "Accommodation",
          };
          setAccommodation(normalized);
        } else {
          setAccommodation(null);
        }
      } catch (error) {
        console.error("Failed to fetch accommodation details:", error);
        setAccommodation(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAccommodation();
  }, [id, state?.accommodation]);

  const images = useMemo(() => {
    if (!accommodation) return [];
    return accommodation?.images?.length
      ? accommodation.images
      : accommodation?.imageUrl
        ? [accommodation.imageUrl]
        : ["https://via.placeholder.com/1200x700?text=Accommodation"];
  }, [accommodation]);

  useEffect(() => {
    setActive(0);
  }, [accommodation?._id, accommodation?.id]);

  const next = () =>
    setActive((prev) => (images.length ? (prev + 1) % images.length : 0));
  const prev = () =>
    setActive((prev) =>
      images.length ? (prev - 1 + images.length) % images.length : 0,
    );

  if (loading) {
    return (
      <Box p={6} color={theme.text}>
        <HStack>
          <GlobalSpinner />
          <Text>Loading accommodation details...</Text>
        </HStack>
      </Box>
    );
  }

  if (!accommodation) {
    return (
      <Box p={6} color={theme.text}>
        <Box
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          p={5}
          boxShadow={theme.shadowSm}
        >
          <Text mb={4} color={theme.text}>
            No accommodation details found.
          </Text>
          <Button
            onClick={() => navigate("/book/accommodation")}
            color={theme.textOnPrimary}
            bgImage={theme.gradients.primaryButton}
            _hover={{ bgImage: theme.gradients.primaryButtonHover }}
            boxShadow={theme.shadowSm}
          >
            Back to Accommodations
          </Button>
        </Box>
      </Box>
    );
  }

  const accommodationShareId = String(
    accommodation?._id || accommodation?.id || id || "",
  ).trim();

  const accommodationShareUrl = accommodationShareId
    ? `${PICKDENGU_PUBLIC_WEB_URL}/travel/accommodations/${encodeURIComponent(
        accommodationShareId,
      )}`
    : PICKDENGU_PUBLIC_WEB_URL;

  const accommodationShareText = [
    accommodation?.name || "Accommodation",
    accommodation?.location,
    `MWK ${Number(
      accommodation?.pricePerNight ?? accommodation?.price ?? 0,
    ).toLocaleString()} per night`,
    accommodation?.description?.trim()?.slice(0, 160),
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <Box p={{ base: 4, md: 6 }} color={theme.text}>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between" align="start" flexWrap="wrap">
          <VStack align="start" spacing={1}>
            <Text
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="bold"
              color={theme.text}
            >
              {accommodation?.name || "Accommodation"}
            </Text>
            <Text color={theme.textSubtle}>
              {accommodation?.location || "-"}
            </Text>
          </VStack>

          <HStack spacing={2}>
            <Badge
              colorScheme={
                accommodation?.availability === false ? "red" : "green"
              }
            >
              {accommodation?.availability === false
                ? "Unavailable"
                : "Available"}
            </Badge>
            <Badge colorScheme="purple">
              {accommodation?.type || accommodation?.roomType || "Room"}
            </Badge>
          </HStack>
        </HStack>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          <VStack align="stretch" spacing={3}>
            <Box
              borderWidth="1px"
              borderColor={theme.border}
              borderRadius={cardsBorderRadii}
              overflow="hidden"
              height={{ base: "260px", md: "420px" }}
              bg={theme.surfaceAlt}
            >
              <Image
                src={images[active]}
                alt={accommodation?.name || "Accommodation"}
                w="100%"
                h="100%"
                objectFit="cover"
              />
            </Box>

            <HStack justify="space-between">
              <Button
                variant="outline"
                onClick={prev}
                borderColor={theme.borderStrong}
                color={theme.text}
                _hover={{ bg: theme.hover }}
              >
                Prev
              </Button>

              <Text fontSize="sm" color={theme.textMuted}>
                {images.length ? active + 1 : 0} / {images.length}
              </Text>

              <Button
                variant="outline"
                onClick={next}
                borderColor={theme.borderStrong}
                color={theme.text}
                _hover={{ bg: theme.hover }}
              >
                Next
              </Button>
            </HStack>

            <HStack spacing={2} overflowX="auto" py={1}>
              {images.map((src: string, idx: number) => (
                <Box
                  key={`${src}-${idx}`}
                  borderWidth={idx === active ? "2px" : "1px"}
                  borderColor={idx === active ? theme.primary : theme.border}
                  borderRadius={cardsBorderRadii}
                  overflow="hidden"
                  cursor="pointer"
                  flex="0 0 auto"
                  onClick={() => setActive(idx)}
                  bg={theme.surface}
                >
                  <Image
                    src={src}
                    alt={`${accommodation?.name || "Accommodation"}-${idx}`}
                    boxSize="62px"
                    objectFit="cover"
                  />
                </Box>
              ))}
            </HStack>
          </VStack>

          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between" align="start" flexWrap="wrap">
              <Text fontSize="2xl" fontWeight="bold" color={theme.text}>
                MWK{" "}
                {Number(
                  accommodation?.pricePerNight ?? accommodation?.price ?? 0,
                ).toLocaleString()}{" "}
                <Text
                  as="span"
                  fontSize="sm"
                  fontWeight="normal"
                  color={theme.textSubtle}
                >
                  / night
                </Text>
              </Text>

              <HStack spacing={2} flexWrap="nowrap">
                <Button
                  w="150px"
                  onClick={onOpenBookingModal}
                  color={theme.textOnPrimary}
                  bgImage={theme.gradients.primaryButton}
                  boxShadow={theme.shadowSm}
                  _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                  isDisabled={accommodation?.availability === false}
                  whiteSpace="nowrap"
                >
                  Book now
                </Button>

                <PickDenguShareButton
                  shareTitle={`${
                    accommodation?.name || "Accommodation"
                  } on PickDengu`}
                  shareText={accommodationShareText}
                  shareUrl={accommodationShareUrl}
                  modalTitle={`Share ${accommodation?.name || "Accommodation"}`}
                  modalDescription="Share this accommodation details page."
                  buttonProps={{
                    w: "115px",
                    variant: "outline",
                    borderColor: theme.borderStrong,
                    color: theme.text,
                    bg: theme.surface,
                    boxShadow: theme.shadowSm,
                    borderRadius: {cardsBorderRadii},
                    _hover: {
                      bg: theme.hover,
                    },
                  }}
                />
              </HStack>
            </HStack>

            <Text color={theme.textMuted}>
              {accommodation?.description || "No description provided."}
            </Text>

            <Divider borderColor={theme.divider} />

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <Info
                label="Price / night"
                value={`MWK ${Number(accommodation?.pricePerNight ?? accommodation?.price ?? 0).toLocaleString()}`}
              />
              <Info
                label="Capacity"
                value={`${accommodation?.capacity ?? accommodation?.guests ?? "-"} guests`}
              />
              <Info
                label="Bedrooms"
                value={
                  accommodation?.bedrooms != null
                    ? String(accommodation.bedrooms)
                    : "-"
                }
              />
              <Info
                label="Bathrooms"
                value={
                  accommodation?.bathrooms != null
                    ? String(accommodation.bathrooms)
                    : "-"
                }
              />
              <Info
                label="Type"
                value={
                  accommodation?.type ||
                  accommodation?.roomType ||
                  "Accommodation"
                }
              />
              <Info
                label="Availability"
                value={
                  accommodation?.availability === false
                    ? "Unavailable"
                    : "Available"
                }
              />
            </SimpleGrid>

            <Divider borderColor={theme.divider} />

            <Box
              borderWidth="1px"
              borderColor={theme.border}
              borderRadius={cardsBorderRadii}
              p={4}
              bg={theme.surfaceAlt}
            >
              <Text fontWeight="semibold" color={theme.text} mb={2}>
                Description
              </Text>
              <Text color={theme.textSubtle}>
                {accommodation?.description || "No description provided."}
              </Text>
            </Box>

            <Button
              onClick={() => navigate("/book/accommodation")}
              variant="outline"
              borderColor={theme.borderStrong}
              color={theme.text}
              _hover={{ bg: theme.hover }}
              alignSelf="flex-start"
            >
              Back
            </Button>
          </VStack>
        </SimpleGrid>
      </VStack>

      <AccommodationDetailsModal
        isOpen={isBookingModalOpen}
        onClose={onCloseBookingModal}
        accommodation={accommodation}
        onBook={({ accommodation, room, checkIn, checkOut, guests }) => {
          const accommodationId = accommodation?._id ?? accommodation?.id ?? id;

          navigate(`/travel/accommodations/checkout/${accommodationId}`, {
            state: {
              accommodation,
              room,
              checkIn,
              checkOut,
              guests,
            },
          });
        }}
      />
    </Box>
  );
};

function Info({ label, value }: { label: string; value?: string }) {
  const { theme } = useCustomTheme();

  return (
    <Box
      borderWidth="1px"
      borderColor={theme.border}
      borderRadius={cardsBorderRadii}
      p={3}
      bg={theme.surface}
    >
      <Text fontSize="xs" color={theme.textSubtle}>
        {label}
      </Text>
      <Text fontWeight="semibold" color={theme.text}>
        {value || "—"}
      </Text>
    </Box>
  );
}

export default AccommodationDetail;
